import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherEdit2 } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AddQuestionnaireConfigDtoAssigneeRolesItem,
  AddQuestionnaireConfigDtoCompletionMode,
  AddQuestionnaireConfigDtoFrequency,
  AddQuestionnaireConfigDtoMissBehaviour,
} from "@/generated/model";
import { NurseryMemberDto } from "@/generated/model/nurseryMemberDto";

export const DAYS = [
  { key: "MO", label: "Mon" },
  { key: "TU", label: "Tue" },
  { key: "WE", label: "Wed" },
  { key: "TH", label: "Thu" },
  { key: "FR", label: "Fri" },
  { key: "SA", label: "Sat" },
  { key: "SU", label: "Sun" },
] as const;

export const ROLES = [
  {
    value: AddQuestionnaireConfigDtoAssigneeRolesItem.MANAGER,
    label: "Manager",
  },
  {
    value: AddQuestionnaireConfigDtoAssigneeRolesItem.DEPUTY_MANAGER,
    label: "Deputy Manager",
  },
  {
    value: AddQuestionnaireConfigDtoAssigneeRolesItem.LEADER,
    label: "Leader",
  },
  {
    value: AddQuestionnaireConfigDtoAssigneeRolesItem.PRACTITIONER,
    label: "Practitioner",
  },
] as const;

export type AssigneeType = "role" | "team_members";

// Sentinel for the "None" primary-category option. Radix Select.Item rejects an
// empty-string value, so we use a non-empty token and map it back to "cleared".
const NO_PRIMARY_CATEGORY = "__none__";

interface ScheduleTabProps {
  // State
  hasExistingConfig: boolean;
  isEditing: boolean;
  isPublished: boolean;
  frequency: AddQuestionnaireConfigDtoFrequency;
  selectedDays: string[];
  dueByType: "end_of_day" | "custom";
  dueByTime: string;
  availableFromTime: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  assigneeType: AssigneeType;
  assigneeRoles: AddQuestionnaireConfigDtoAssigneeRolesItem[];
  assigneeUserIds: string[];
  nurseryMembers: NurseryMemberDto[];
  categories: { id: string; name: string }[];
  primaryCategoryId: string;
  secondaryCategoryIds: string[];
  summary: string[];
  nurseryName: string | undefined;
  missBehaviour: AddQuestionnaireConfigDtoMissBehaviour;
  availableFromDayOfMonth: number;
  dueByDayOfMonth: number;
  availableFromDayOfWeek: string;
  dueByDayOfWeek: string;
  completionMode: AddQuestionnaireConfigDtoCompletionMode;

  // Loading states
  isSaving: boolean;
  isUpdating: boolean;
  isMembersLoading: boolean;

  // Handlers
  setIsEditing: (editing: boolean) => void;
  setFrequency: (frequency: AddQuestionnaireConfigDtoFrequency) => void;
  setSelectedDays: React.Dispatch<React.SetStateAction<string[]>>;
  setDueByType: (type: "end_of_day" | "custom") => void;
  setDueByTime: (time: string) => void;
  setAvailableFromTime: (time: string) => void;
  setStartDate: (date: Date | undefined) => void;
  setEndDate: (date: Date | undefined) => void;
  setAssigneeType: (type: AssigneeType) => void;
  setAssigneeRoles: React.Dispatch<
    React.SetStateAction<AddQuestionnaireConfigDtoAssigneeRolesItem[]>
  >;
  setAssigneeUserIds: React.Dispatch<React.SetStateAction<string[]>>;
  setPrimaryCategoryId: React.Dispatch<React.SetStateAction<string>>;
  setSecondaryCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
  setMissBehaviour: (behaviour: AddQuestionnaireConfigDtoMissBehaviour) => void;
  setAvailableFromDayOfMonth: (day: number) => void;
  setDueByDayOfMonth: (day: number) => void;
  setAvailableFromDayOfWeek: (day: string) => void;
  setDueByDayOfWeek: (day: string) => void;
  setCompletionMode: (mode: AddQuestionnaireConfigDtoCompletionMode) => void;
  onSave: () => void;
  onUpdate: () => void;
}

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
function isValidTime(value: string): boolean {
  return TIME_REGEX.test(value);
}

function TimeInput({
  value,
  onChange,
  placeholder,
  error,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error: boolean;
  className?: string;
}) {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <div className={`relative ${className ?? "w-24 flex-none"}`}>
      <TextField className="h-auto w-full" size="large" error={showError}>
        <TextField.Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </TextField>
      {showError && (
        <span className="absolute -bottom-5 left-0 text-[10px] font-caption text-error-700">
          HH:MM
        </span>
      )}
    </div>
  );
}

export function ScheduleTab({
  hasExistingConfig,
  isEditing,
  isPublished,
  frequency,
  selectedDays,
  dueByType,
  dueByTime,
  availableFromTime,
  startDate,
  endDate,
  assigneeType,
  assigneeRoles,
  assigneeUserIds,
  nurseryMembers,
  categories,
  primaryCategoryId,
  secondaryCategoryIds,
  summary,
  nurseryName,
  missBehaviour,
  availableFromDayOfMonth,
  dueByDayOfMonth,
  availableFromDayOfWeek,
  dueByDayOfWeek,
  completionMode,
  isSaving,
  isUpdating,
  isMembersLoading,
  setIsEditing,
  setFrequency,
  setSelectedDays: _setSelectedDays,
  setDueByType,
  setDueByTime,
  setAvailableFromTime,
  setStartDate,
  setEndDate,
  setAssigneeType,
  setAssigneeRoles,
  setAssigneeUserIds,
  setPrimaryCategoryId,
  setSecondaryCategoryIds,
  setMissBehaviour,
  setAvailableFromDayOfMonth,
  setDueByDayOfMonth,
  setAvailableFromDayOfWeek,
  setDueByDayOfWeek,
  setCompletionMode,
  onSave,
  onUpdate,
}: ScheduleTabProps) {
  const navigate = useNavigate();

  const handleCancel = () => {
    if (hasExistingConfig) {
      setIsEditing(false);
    } else {
      navigate({ to: "/templates" });
    }
  };

  const handleSave = () => {
    if (hasExistingConfig) {
      onUpdate();
    } else {
      onSave();
    }
  };

  // Get the first selected role for the select (single select in the design)
  const selectedRole = assigneeRoles[0] || "";

  // Format assignees for display
  const formatAssignees = () => {
    if (assigneeType === "team_members") {
      if (assigneeUserIds.length === 0) return "Not assigned";
      const names = assigneeUserIds
        .map((id) => {
          const member = nurseryMembers.find((m) => m.userId === id);
          if (!member) return null;
          return (
            [member.firstName, member.lastName].filter(Boolean).join(" ") ||
            member.email
          );
        })
        .filter(Boolean);
      return names.join(", ");
    }
    if (assigneeRoles.length === 0) return "Not assigned";
    return assigneeRoles
      .map((r) => ROLES.find((role) => role.value === r)?.label)
      .join(", ");
  };

  // Toggle team member selection
  const toggleMemberSelection = (userId: string) => {
    setAssigneeUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  // Toggle a secondary category (checklists carry a primary + zero or more secondaries).
  const toggleCategory = (categoryId: string, checked: boolean) => {
    setSecondaryCategoryIds((prev) =>
      checked
        ? prev.includes(categoryId)
          ? prev
          : [...prev, categoryId]
        : prev.filter((id) => id !== categoryId),
    );
  };

  // Primary and secondary are independent. Choosing a primary only removes it
  // from the secondary set (a category can't be both, which would also duplicate
  // it in the saved categoryIds); "None" clears the primary and leaves the
  // secondaries untouched. (Radix Select.Item rejects an empty-string value, so
  // we can't use "" for the "None" option.)
  const selectPrimaryCategory = (categoryId: string) => {
    if (!categoryId || categoryId === NO_PRIMARY_CATEGORY) {
      setPrimaryCategoryId("");
      return;
    }
    setSecondaryCategoryIds((prev) => prev.filter((id) => id !== categoryId));
    setPrimaryCategoryId(categoryId);
  };

  const isAvailableFromTimeValid = isValidTime(availableFromTime);
  const isDueByTimeValid = isValidTime(dueByTime);
  const hasTimeErrors =
    !isAvailableFromTimeValid ||
    (!isDueByTimeValid &&
      (frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME ||
        frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY ||
        dueByType === "custom"));

  const isOneTime = frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME;
  const isWeekly = frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY;

  // Day-of-week index for validation (MO=0 .. SU=6)
  const DAY_INDEX: Record<string, number> = {
    MO: 0,
    TU: 1,
    WE: 2,
    TH: 3,
    FR: 4,
    SA: 5,
    SU: 6,
  };
  const isWeeklyWindowInvalid =
    isWeekly &&
    (() => {
      const fromIdx = DAY_INDEX[availableFromDayOfWeek] ?? 0;
      const toIdx = DAY_INDEX[dueByDayOfWeek] ?? 0;
      if (fromIdx === toIdx) {
        // Same day: due time must be after available time
        if (dueByType === "end_of_day") return false;
        return dueByTime <= availableFromTime;
      }
      // Due day must be later in the week than from day
      return toIdx < fromIdx;
    })();

  const isSaveDisabled =
    isSaving ||
    isUpdating ||
    hasTimeErrors ||
    isWeeklyWindowInvalid ||
    (isOneTime && (!startDate || !endDate));

  // Format days for display
  const formatDays = () => {
    if (isWeekly && availableFromDayOfWeek && dueByDayOfWeek) {
      const fromLabel =
        DAYS.find((d) => d.key === availableFromDayOfWeek)?.label ||
        availableFromDayOfWeek;
      const toLabel =
        DAYS.find((d) => d.key === dueByDayOfWeek)?.label || dueByDayOfWeek;
      return `${fromLabel} to ${toLabel}`;
    }
    return selectedDays
      .map((d) => DAYS.find((day) => day.key === d)?.label)
      .join(", ");
  };

  // Published view (read-only)
  if (isPublished && !isEditing) {
    return (
      <div className="flex w-full items-center justify-center">
        <div className="flex w-256 max-w-full flex-none flex-col items-start gap-6 rounded-lg bg-default-background px-8 pt-8 pb-6 mb-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-primary-navy-800">
              Current schedule
            </span>
            <Badge variant="success">Schedule in use</Badge>
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            {/* First row */}
            <div className="flex w-full items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Frequency
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME
                    ? "One-time"
                    : frequency === AddQuestionnaireConfigDtoFrequency.DAILY
                      ? "Every day"
                      : frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY
                        ? "Monthly"
                        : "Every week"}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Duration
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {startDate && endDate
                    ? `${format(startDate, "d MMMM yyyy")} to ${format(endDate, "d MMMM yyyy")}`
                    : "Not set"}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Days
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {formatDays()}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Assigned to
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {formatAssignees()}
                </span>
              </div>
            </div>

            {/* Second row */}
            <div className="flex w-full items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Due by
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {dueByType === "end_of_day" ? "End of day" : dueByTime}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Available from
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {availableFromTime}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch">
                <span className="text-caption font-caption text-subtext-color">
                  Nurseries
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {nurseryName || "All nurseries"}
                </span>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 self-stretch" />
            </div>

            {/* Categories — primary first (brand), then secondaries (neutral) */}
            {primaryCategoryId && (
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-caption font-caption text-subtext-color">
                  Categories
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {[primaryCategoryId, ...secondaryCategoryIds].map((id) => {
                    const category = categories.find((c) => c.id === id);
                    return (
                      <Badge
                        key={id}
                        variant={id === primaryCategoryId ? "brand" : "neutral"}
                      >
                        {category?.name ?? "Unknown"}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

          {/* Summary */}
          <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-4">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Summary
            </span>
            {summary.map((line, i) => (
              <span key={i} className="text-body font-body text-neutral-700">
                {line}
              </span>
            ))}
          </div>

          {/* Edit button */}
          <div className="flex w-full flex-col items-end gap-6">
            <Button
              variant="neutral-secondary"
              size="large"
              icon={<FeatherEdit2 />}
              onClick={() => setIsEditing(true)}
            >
              Edit schedule
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex w-256 max-w-full flex-none flex-col items-start rounded-lg bg-default-background mb-8 relative">
        <div className="flex w-full flex-col items-start gap-6 px-8 pt-8 pb-24">
          <div className="flex w-full flex-col items-start pb-2">
            <span className="text-heading-2 font-heading-2 text-primary-navy-800">
              {hasExistingConfig && !isEditing
                ? "Current schedule"
                : "Set a schedule"}
            </span>
          </div>

          {/* Repeat */}
          <div className="flex w-full items-center gap-4">
            <span className="min-w-[56px] text-body-bold font-body-bold text-neutral-900">
              Repeat
            </span>
            <div className="flex grow shrink-0 basis-0 items-center gap-4">
              <Select
                className="h-10 grow shrink-0 basis-0"
                variant="outline"
                placeholder="Weekly"
                size="large"
                value={frequency}
                onValueChange={(value) =>
                  setFrequency(value as AddQuestionnaireConfigDtoFrequency)
                }
              >
                <Select.Item value="ONE_TIME" size="large">
                  One-time
                </Select.Item>
                <Select.Item value="DAILY" size="large">
                  Daily (Mon to Fri)
                </Select.Item>
                <Select.Item value="WEEKLY" size="large">
                  Weekly
                </Select.Item>
                <Select.Item value="MONTHLY" size="large">
                  Monthly
                </Select.Item>
              </Select>
            </div>
          </div>

          {/* Available from / Due by - for WEEKLY (day-of-week + time) */}
          {frequency === AddQuestionnaireConfigDtoFrequency.WEEKLY && (
            <div className="flex w-full items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Available from
                </span>
                <div className="flex w-full items-start gap-2">
                  <Select
                    className="h-10 grow shrink-0 basis-0"
                    variant="outline"
                    size="large"
                    value={availableFromDayOfWeek}
                    onValueChange={(value) => setAvailableFromDayOfWeek(value)}
                  >
                    {DAYS.map((day) => (
                      <Select.Item key={day.key} value={day.key} size="large">
                        {day.label}
                      </Select.Item>
                    ))}
                  </Select>
                  <TimeInput
                    value={availableFromTime}
                    onChange={setAvailableFromTime}
                    placeholder="09:00"
                    error={!isAvailableFromTimeValid}
                  />
                </div>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Due by
                </span>
                <div className="flex w-full items-start gap-2">
                  <Select
                    className="h-10 grow shrink-0 basis-0"
                    variant="outline"
                    size="large"
                    value={dueByDayOfWeek}
                    onValueChange={(value) => setDueByDayOfWeek(value)}
                  >
                    {DAYS.map((day) => (
                      <Select.Item key={day.key} value={day.key} size="large">
                        {day.label}
                      </Select.Item>
                    ))}
                  </Select>
                  <Select
                    className="h-10 w-32 flex-none"
                    variant="outline"
                    size="large"
                    value={dueByType}
                    onValueChange={(value) =>
                      setDueByType(value as "end_of_day" | "custom")
                    }
                  >
                    <Select.Item value="end_of_day" size="large">
                      End of day
                    </Select.Item>
                    <Select.Item value="custom" size="large">
                      Custom
                    </Select.Item>
                  </Select>
                  {dueByType === "custom" && (
                    <TimeInput
                      value={dueByTime}
                      onChange={setDueByTime}
                      placeholder="18:00"
                      error={!isDueByTimeValid}
                    />
                  )}
                </div>
                {isWeeklyWindowInvalid && (
                  <span className="text-caption font-caption text-error-700">
                    Due day/time must be after the available from day/time
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Starts and Due by - for ONE_TIME, DAILY */}
          {frequency !== AddQuestionnaireConfigDtoFrequency.MONTHLY &&
            frequency !== AddQuestionnaireConfigDtoFrequency.WEEKLY && (
              <div className="flex w-full items-start gap-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME
                      ? "Start date"
                      : "Available from"}
                  </span>
                  {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME ? (
                    <div className="flex w-full items-start gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex h-10 grow shrink-0 basis-0 items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
                          >
                            <span>
                              {startDate
                                ? format(startDate, "EEEE, MMMM d, yyyy")
                                : "Select date"}
                            </span>
                            <svg
                              className="h-4 w-4 text-neutral-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={setStartDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <TimeInput
                        value={availableFromTime}
                        onChange={setAvailableFromTime}
                        placeholder="09:00"
                        error={!isAvailableFromTimeValid}
                      />
                    </div>
                  ) : (
                    <TimeInput
                      value={availableFromTime}
                      onChange={setAvailableFromTime}
                      placeholder="09:00"
                      error={!isAvailableFromTimeValid}
                      className="w-full"
                    />
                  )}
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME
                      ? "Due date"
                      : "Due by"}
                  </span>
                  {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME ? (
                    <div className="flex w-full items-start gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex h-10 grow shrink-0 basis-0 items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
                          >
                            <span>
                              {endDate
                                ? format(endDate, "EEEE, MMMM d, yyyy")
                                : "Select date"}
                            </span>
                            <svg
                              className="h-4 w-4 text-neutral-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={endDate}
                            onSelect={setEndDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <TimeInput
                        value={dueByTime}
                        onChange={setDueByTime}
                        placeholder="18:00"
                        error={!isDueByTimeValid}
                      />
                    </div>
                  ) : (
                    <div className="flex w-full items-center gap-2">
                      <Select
                        className="h-10 grow shrink-0 basis-0"
                        variant="outline"
                        placeholder="End of day"
                        size="large"
                        value={dueByType}
                        onValueChange={(value) =>
                          setDueByType(value as "end_of_day" | "custom")
                        }
                      >
                        <Select.Item value="end_of_day" size="large">
                          End of day
                        </Select.Item>
                        <Select.Item value="custom" size="large">
                          Custom time
                        </Select.Item>
                      </Select>
                      {dueByType === "custom" && (
                        <TimeInput
                          value={dueByTime}
                          onChange={setDueByTime}
                          placeholder="18:00"
                          error={!isDueByTimeValid}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Monthly day pickers */}
          {frequency === AddQuestionnaireConfigDtoFrequency.MONTHLY && (
            <div className="flex w-full items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Available from day
                </span>
                <div className="flex w-full items-start gap-2">
                  <Select
                    className="h-10 grow shrink-0 basis-0"
                    variant="outline"
                    size="large"
                    value={String(availableFromDayOfMonth)}
                    onValueChange={(value) =>
                      setAvailableFromDayOfMonth(Number(value))
                    }
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <Select.Item key={day} value={String(day)} size="large">
                        Day {day}
                      </Select.Item>
                    ))}
                  </Select>
                  <TimeInput
                    value={availableFromTime}
                    onChange={setAvailableFromTime}
                    placeholder="09:00"
                    error={!isAvailableFromTimeValid}
                  />
                </div>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Due by day
                </span>
                <div className="flex w-full items-start gap-2">
                  <Select
                    className="h-10 grow shrink-0 basis-0"
                    variant="outline"
                    size="large"
                    value={String(dueByDayOfMonth)}
                    onValueChange={(value) => setDueByDayOfMonth(Number(value))}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <Select.Item key={day} value={String(day)} size="large">
                        Day {day}
                      </Select.Item>
                    ))}
                  </Select>
                  <TimeInput
                    value={dueByTime}
                    onChange={setDueByTime}
                    placeholder="18:00"
                    error={!isDueByTimeValid}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Runs from and Stops running on - hidden for ONE_TIME */}
          {frequency !== AddQuestionnaireConfigDtoFrequency.ONE_TIME && (
            <div className="flex w-full items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Runs from
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex h-10 w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
                    >
                      <span>
                        {startDate
                          ? format(startDate, "EEEE, MMMM d, yyyy")
                          : "Select date"}
                      </span>
                      <svg
                        className="h-4 w-4 text-neutral-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-neutral-900">
                  Stops running on
                  <span className="text-caption font-caption text-subtext-color ml-1">
                    (optional)
                  </span>
                </span>
                <div className="flex w-full items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex h-10 grow items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
                      >
                        <span className={endDate ? "" : "text-neutral-400"}>
                          {endDate
                            ? format(endDate, "EEEE, MMMM d, yyyy")
                            : "No end date"}
                        </span>
                        <svg
                          className="h-4 w-4 text-neutral-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {endDate && (
                    <button
                      type="button"
                      className="flex h-10 w-10 flex-none items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
                      onClick={() => setEndDate(undefined)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Miss behaviour */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-neutral-900">
              If missed
            </span>
            <div className="flex w-full items-center gap-2">
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  missBehaviour ===
                  AddQuestionnaireConfigDtoMissBehaviour.LATE_ALLOWED
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() =>
                  setMissBehaviour(
                    AddQuestionnaireConfigDtoMissBehaviour.LATE_ALLOWED,
                  )
                }
              >
                Late completion allowed
              </Button>
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  missBehaviour ===
                  AddQuestionnaireConfigDtoMissBehaviour.HARD_MISS
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() =>
                  setMissBehaviour(
                    AddQuestionnaireConfigDtoMissBehaviour.HARD_MISS,
                  )
                }
              >
                Hard miss
              </Button>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              {missBehaviour ===
              AddQuestionnaireConfigDtoMissBehaviour.LATE_ALLOWED
                ? "Staff can still complete this checklist after the deadline has passed."
                : "This checklist cannot be completed once the deadline has passed."}
            </span>
          </div>

          {/* Which nurseries */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Which nurseries is this scheduled for?
            </span>
            <div className="flex h-10 w-full items-center rounded-md border border-solid border-neutral-border bg-neutral-100 px-3 text-body font-body text-neutral-500">
              {nurseryName || "All nurseries"}
            </div>
          </div>

          {/* Categories — a primary classification plus optional secondaries.
              No wrapper header (matches the compliance task form). */}
          <div className="flex w-full flex-col items-start gap-4">
            {categories.length === 0 ? (
              <span className="text-body font-body text-subtext-color">
                No categories available
              </span>
            ) : (
              <>
                {/* Primary category */}
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    Primary category
                    <span className="text-caption font-caption text-subtext-color ml-1">
                      (optional)
                    </span>
                  </span>
                  <Select
                    className="h-10 w-full"
                    variant="outline"
                    placeholder="Select a primary category"
                    size="large"
                    // Bind to the sentinel (not undefined) when there's no primary
                    // so selecting "None" flips the controlled value to a real item
                    // and the trigger updates — a controlled Radix Select doesn't
                    // reset its display when value goes back to undefined.
                    value={primaryCategoryId || NO_PRIMARY_CATEGORY}
                    onValueChange={selectPrimaryCategory}
                  >
                    <Select.Item value={NO_PRIMARY_CATEGORY} size="large">
                      None
                    </Select.Item>
                    {categories.map((category) => (
                      <Select.Item
                        key={category.id}
                        value={category.id}
                        size="large"
                      >
                        {category.name}
                      </Select.Item>
                    ))}
                  </Select>
                  <span className="text-caption font-caption text-subtext-color">
                    The main classification for this checklist.
                  </span>
                </div>

                {/* Secondary categories */}
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    Secondary categories
                    <span className="text-caption font-caption text-subtext-color ml-1">
                      (optional)
                    </span>
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Optionally classify this checklist under additional
                    categories.
                  </span>
                  <div className="w-full columns-2 gap-x-4 pt-1">
                    {categories
                      .filter((category) => category.id !== primaryCategoryId)
                      .map((category) => (
                        <div
                          key={category.id}
                          className="mb-2 break-inside-avoid"
                        >
                          <Checkbox
                            label={category.name}
                            checked={secondaryCategoryIds.includes(category.id)}
                            onCheckedChange={(checked) =>
                              toggleCategory(category.id, checked)
                            }
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Who is this checklist for */}
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Who is this checklist for?
            </span>

            {/* Assignment type toggle */}
            <div className="flex w-full items-center gap-2">
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  assigneeType === "role"
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() => setAssigneeType("role")}
              >
                Role
              </Button>
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  assigneeType === "team_members"
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() => setAssigneeType("team_members")}
              >
                Team members
              </Button>
            </div>

            {/* Role selector */}
            {assigneeType === "role" && (
              <Select
                className="h-10 w-full"
                variant="outline"
                placeholder="Select role"
                size="large"
                value={selectedRole}
                onValueChange={(value) =>
                  setAssigneeRoles([
                    value as AddQuestionnaireConfigDtoAssigneeRolesItem,
                  ])
                }
              >
                {ROLES.map((role) => (
                  <Select.Item key={role.value} value={role.value} size="large">
                    {role.label}
                  </Select.Item>
                ))}
              </Select>
            )}

            {/* Team members multi-select */}
            {assigneeType === "team_members" && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background p-3 max-h-[240px] overflow-y-auto">
                {isMembersLoading ? (
                  <span className="text-body font-body text-subtext-color">
                    Loading team members...
                  </span>
                ) : nurseryMembers.length === 0 ? (
                  <span className="text-body font-body text-subtext-color">
                    No team members found
                  </span>
                ) : (
                  nurseryMembers.map((member) => {
                    const displayName =
                      [member.firstName, member.lastName]
                        .filter(Boolean)
                        .join(" ") || member.email;
                    const isSelected = assigneeUserIds.includes(member.userId);
                    return (
                      <div
                        key={member.userId}
                        className="flex w-full items-center gap-3 py-2 px-1 rounded-md hover:bg-neutral-50 cursor-pointer"
                        onClick={() => toggleMemberSelection(member.userId)}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex flex-col items-start">
                          <span className="text-body-bold font-body-bold text-neutral-900">
                            {displayName}
                          </span>
                          <span className="text-caption font-caption text-subtext-color">
                            {ROLES.find(
                              (r) => r.value === (member.role as string),
                            )?.label || member.role}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Completion mode */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Completion
            </span>
            <div className="flex w-full items-center gap-2">
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  completionMode === AddQuestionnaireConfigDtoCompletionMode.ANY
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() =>
                  setCompletionMode(AddQuestionnaireConfigDtoCompletionMode.ANY)
                }
              >
                Any member can complete
              </Button>
              <Button
                className="h-10 grow shrink-0 basis-0"
                variant={
                  completionMode ===
                  AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL
                    ? "brand-primary"
                    : "neutral-secondary"
                }
                size="large"
                onClick={() =>
                  setCompletionMode(
                    AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL,
                  )
                }
              >
                Each member must complete
              </Button>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              {completionMode ===
              AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL
                ? "A separate checklist instance will be created for each assigned member."
                : "A single shared checklist that any assigned member can complete."}
            </span>
          </div>

          {/* Summary */}
          <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-4">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Summary
            </span>
            {summary.map((line, i) => (
              <span key={i} className="text-body font-body text-neutral-700">
                {line}
              </span>
            ))}
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex h-20 w-full flex-none items-center justify-between rounded-b-lg border-t border-solid border-neutral-200 bg-default-background px-8 absolute bottom-0 left-0 right-0">
          {isEditing ? (
            <Button
              variant="neutral-secondary"
              size="large"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          ) : (
            <div />
          )}

          <Button
            variant="brand-primary"
            size="large"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {isSaving || isUpdating ? "Saving..." : "Save schedule"}
          </Button>
        </div>
      </div>
    </div>
  );
}
